import React, { Component, Fragment, cloneElement } from 'react';
import { number, string, shape, arrayOf, oneOfType, element, func } from 'prop-types';
import { includes } from 'lodash';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Paper from '@material-ui/core/Paper';

import classNames from 'classnames';
import styles from './styles.scss';

/**
 * Autocomplete Selector Component
 * @param {string} className a class name to override basic styling
 * @param {string} placeholder placeholder string for empty input
 * @param {Array<Object>} dataPool a list of all available entries. This is used as hinting options
 * @param {Array} selectedIds a list of all selected ids (an array of either strings or numbers)
 * @param {Array(string)} searchableProps a list of searchable prop names (an array of strings)
 * @param {element} selectionTemplate react element that is used as a blueprint to generate hint
 * @param {element} selectedEntryTemplate react element that is used as a blueprint to generate selected entry bodies (must have `onDelete(id)` callback)
 * @param {function} onChange a callback function with the new selectedIds array. This is triggered when an entry is selected or removed
 */
export default class AutocompleteSelector extends Component {
  static propTypes = {
    className: string,
    placeholder: string,
    dataPool: arrayOf(shape({
      id: oneOfType([number, string]).isRequired
    })),
    selectedIds: arrayOf(oneOfType([number, string])),
    searchableProps: arrayOf(string),
    selectionTemplate: element.isRequired,
    selectedEntryTemplate: element.isRequired,
    maxHints: number,
    onChange: func
  }

  static defaultProps = {
    maxHints: 30,
    placeholder: '',
    dataPool: [],
    selectedIds: [],
    searchableProps: [],
  }

  state = {
    searchString: ''
  }
  
  //---------------------------------------------------------------------
  //------------------------Interaction Functions------------------------
  //---------------------------------------------------------------------
  handleAdd = value => event => {
    const { onChange, selectedIds } = this.props;
    const newSelectedIds = [...selectedIds];
    !includes(newSelectedIds, value) && newSelectedIds.push(value);
    onChange && onChange(newSelectedIds);

    this.textInputRef.current.value = '';
    this.textInputRef.current.focus();
    this.handleSearchChange();
  }

  handleRemove = (value) => {
    const { onChange, selectedIds } = this.props;
    const newSelectedIds = selectedIds.filter(id => {
      return id !== value;
    });

    onChange && onChange(newSelectedIds);
  }

  handleSearchChange = () => {
    const searchString = this.textInputRef.current.value;
    this.setState({searchString});
  }

  handleInputFocus = () => {
    this.textInputRef.current.focus();
  }

  //---------------------------------------------------------------------
  //---------------------------Draw Functions----------------------------
  //---------------------------------------------------------------------
  textInputRef = React.createRef();
  drawInput() {
    const {
      placeholder,
      dataPool,
      selectedIds,
      selectedEntryTemplate,
    } = this.props;
    const { searchString } = this.state;
    const selectedEntries = dataPool.filter(entry => {
      return includes(selectedIds, entry.id);
    });

    const inputWidth = (selectedIds && selectedIds.length > 0) ? `${(searchString.length +1) * 10}px`: '100%';

    return (
      <div className={styles.searchField} onClick={this.handleInputFocus}>
        <Fragment>
          {
            selectedEntries && selectedEntries.map(entry => {
              return cloneElement(selectedEntryTemplate, {...entry, onDelete: this.handleRemove, key: entry.id});
            })
          }
        </Fragment>
        <input 
          data-test="autoCompleteInput"
          type="text"
          ref={this.textInputRef}
          placeholder={(selectedIds && selectedIds.length > 0) ? '' : placeholder}
          style={{width:inputWidth}}
          onChange={this.handleSearchChange}
        />
      </div>
    );
  }
  
  drawHints() {
    const {
      dataPool,
      selectedIds,
      searchableProps,
      selectionTemplate,
      maxHints
    } = this.props;

    const { searchString } = this.state;
    const matchedItems = [];
    for(let i = 0, numData = dataPool.length ; i < numData; i++) {
      const entryData = dataPool[i];
      if(includes(selectedIds, entryData.id)) {
        continue;
      }
      for (let propIndex = 0, numProps = searchableProps.length; propIndex < numProps; propIndex++) {
        const propName = searchableProps[propIndex];
        const propValue = entryData[propName] || '';
        const normalizedPropValue = propValue.toLocaleLowerCase().trim();
        const normalizedString = searchString.toLocaleLowerCase().trim();
        if (normalizedString.length > 0 && normalizedPropValue.includes(normalizedString)) {
          matchedItems.push(entryData);
          break;
        }
      }
      if(matchedItems.length === maxHints) {
        break;
      }
    }

    return (
      <Fragment>
      {
        matchedItems.length > 0 &&
        <Paper className={styles.hints}>
          <List  className={styles.list}>
          {
            matchedItems.map(entryData => {
              return (
                <ListItem
                  data-test="autoCompleteItem"
                  button
                  key={entryData.id}
                  onClick={this.handleAdd(entryData.id)}
                >
                  { cloneElement(selectionTemplate, entryData) }
                </ListItem>
              )
            })
          }
          </List>
        </Paper>
      }
      </Fragment>
    );
  }

  render() {
    const { className } = this.props;

    return (
      <div className={classNames(className, styles.autocomplete)}>
        {this.drawInput()}
        {this.drawHints()}
      </div>
    );
  }
}
